import React, {useContext, useMemo, useState} from 'react';
import {toast} from 'react-toastify';

import {envIsPublicCloud} from '../../config';
import {
  useDeleteModelMutation,
  useProjectHasProtectedArtifactQuery,
  WorkflowsAdminType,
} from '../../generated/graphql';
import {
  TEAM_PAGE_PROJECTS_QUERY,
  TEAM_PAGE_QUERY,
} from '../../graphql/teamPage';
import {DEFAULT_ONPREM_ORGANIZATION_NAME} from '../../pages/OrgDashboard/OrgRoles/RolesDashboard';
import {Analytics} from '../../services/analytics';
import type {ProjectData} from '../../state/graphql/projectPageQuery';
import {useViewer} from '../../state/viewer/hooks';
import {
  doNotRetryContext,
  extractErrorMessageFromApolloError,
  propagateErrorsContext,
} from '../../util/errors';
import {useStatsigGateSourceArtifactProtection} from '../../util/rampFeatureFlags';
import {
  PROJECTS_BY_TEAM_QUERY,
  PROJECTS_BY_USER_QUERY,
} from '../PaginatedList/projectQuery.query';
import {AccountSelectorContext} from '../Search/SearchNav/AccountSelectorContextProvider';
import {
  DELETE_CONFIRM_TEXT,
  StorageConfirmDeleteModal,
} from '../StorageConfirmDeleteModal';
export interface DeleteProjectModalProps {
  isOpen: boolean;
  project: Pick<ProjectData, 'name' | 'entityName' | 'id'>;
  setIsModalOpen: (isOpen: boolean) => void;
  handleModalClose(): void;
}

export const DeleteProjectModal = ({
  isOpen,
  project,
  setIsModalOpen,
  handleModalClose,
}: DeleteProjectModalProps) => {
  const {entityName, id: projectId, name: projectName} = project;
  const viewer = useViewer();
  const {selectedAccount} = useContext(AccountSelectorContext);
  const orgName = envIsPublicCloud
    ? (selectedAccount?.name ?? '')
    : DEFAULT_ONPREM_ORGANIZATION_NAME;
  const {data: isSourceArtifactProtectionEnabled} =
    useStatsigGateSourceArtifactProtection(orgName);

  const [input, setInput] = useState('');

  const {
    data: projectHasProtectedArtifactData,
    loading: projectHasProtectedArtifactLoading,
  } = useProjectHasProtectedArtifactQuery({
    variables: {entityName, name: projectName},
    skip: !isSourceArtifactProtectionEnabled || !isOpen,
    context: propagateErrorsContext(),
  });

  const hasProtectedArtifact = useMemo(() => {
    const hasProtectedArtifact =
      projectHasProtectedArtifactData?.project?.hasProtectedArtifact ?? false;
    if (hasProtectedArtifact) {
      Analytics.track('source_artifact_protection', {
        location: 'DeleteProjectModal',
        entityName,
        projectName,
      });
    }
    return hasProtectedArtifact;
  }, [projectHasProtectedArtifactData, entityName, projectName]);

  const [deleteProject, deleteProjectStatus] = useDeleteModelMutation({
    context: propagateErrorsContext(),
    ...doNotRetryContext(),
    // Ths modal is used in a variety of places so temporarily do a refetch in multiple places.
    // Ideally, we should just have a single query to refetch all the data we need.
    refetchQueries: [
      {
        query: PROJECTS_BY_USER_QUERY,
        variables: {userName: viewer?.username ?? '', first: 20},
      },
      {
        // Query handles updating Team page Projects tab
        query: PROJECTS_BY_TEAM_QUERY,
        variables: {entityName, first: 20},
      },
      {
        // Query handles updating Team page Overview tab when project is deleted
        query: TEAM_PAGE_PROJECTS_QUERY,
        variables: {entityName},
      },
      {
        // Query used to create project prompt on team page and project cards
        // Handles re-rendering prompt if last project is deleted
        query: TEAM_PAGE_QUERY,
        variables: {entityName, workflowsAdminType: WorkflowsAdminType.Model},
      },
    ],
    awaitRefetchQueries: true,
  });

  const deleteButtonEnabled = useMemo(
    () => input === DELETE_CONFIRM_TEXT && deleteProjectStatus.loading !== true,
    [input, deleteProjectStatus.loading]
  );

  const onDeleteClick = async () => {
    try {
      await deleteProject({variables: {id: projectId}});
      toast('Project has been deleted!');
      handleModalClose();
    } catch (e) {
      console.error(
        'Error trying to delete project: ',
        extractErrorMessageFromApolloError(e)
      );
      toast('Error occured while trying to delete project.');
    }
  };

  return (
    <StorageConfirmDeleteModal
      isOpen={isOpen}
      onOpenChange={open => {
        if (!deleteProjectStatus.loading) {
          setIsModalOpen(open);
          setInput('');
        }
      }}
      summary={
        <>
          1 project (<b>{project.name}</b>). This will include all runs, files,
          and metadata within the <b>{project.name}</b> project
        </>
      }
      onConfirm={onDeleteClick}
      onConfirmChange={e => setInput(e.target.value)}
      isDeleteButtonEnabled={deleteButtonEnabled}
      isMutationLoading={deleteProjectStatus.loading}
      hasProtectedArtifact={hasProtectedArtifact}
      hasProtectedArtifactLoading={projectHasProtectedArtifactLoading}
    />
  );
};
